"use client";
import React from "react";
import { useLocation } from "react-router-dom";
import HeaderAndDescription from "./HeaderAndDescription";
import { PropertyButtons } from "./PropertyComponents/PropertyButtons";
import { PropertyFAQs } from "./PropertyComponents/PropertyFAQs";
import { PropertyTabs } from "./PropertyComponents/PropertyTabs";
import { IPropertyResult, IPlace } from "@/interfaces/property";
import { PropertyPhotos } from "./PropertyComponents/PropertyPhotos";
import { PlacesList } from "./PlacesComponents/PlacesList";
import { PlacesFilters } from "./PlacesComponents/PlacesFilters";
import { useTranslation } from "@/lib/i18n";
import { useBackgroundImagePreloader } from "@/hooks/use-background-image-preloader";
import { usePlaces } from "@/hooks/use-places";
import { PropertyMapSection } from "./PropertyMapSection";

interface PropertyContainerProps {
  property: IPropertyResult;
  onPlaceTypeChange?: (placeType: string) => void;
  onPlaceSelect?: (place: IPlace) => void;
  onPlaceHover?: (placeId: string | null) => void;
  activeTab?: string;
  setActiveTab?: (tab: string) => void;
  selectedFilter?: string;
  setSelectedFilter?: (filter: string) => void;
  placeType?: string;
}

const PropertyContainer: React.FC<PropertyContainerProps> = ({
  property,
  onPlaceTypeChange,
  onPlaceSelect,
  onPlaceHover,
  activeTab = "property",
  setActiveTab = () => {},
  selectedFilter = "all",
  setSelectedFilter = () => {},
  placeType = "all",
}) => {
  const { t } = useTranslation();
    
    // Preload all property images in the background
    const allImages = React.useMemo(() => {
      const images: string[] = [];
      
      // Add main property photo
      if (property.photo?.url) {
        images.push(property.photo.url);
      }
      
      // Add all property photos
      property.photos.forEach(photo => {
        if (photo.url) {
          images.push(photo.url);
        }
      });
      
      return images;
    }, [property]);

    useBackgroundImagePreloader(allImages);

    const handleTabChange = (tab: string) => {
      setActiveTab(tab);
    };

    // Places category filters
    const categoryIDs = ["food", "shopping", "attraction", "services", "emergency"];
    
    // Use category IDs directly - translations will be handled in PlacesFilters component
    const categories = ["all", ...categoryIDs];

    const handleFilterChange = (filter: string) => {
      setSelectedFilter(filter);
      // Notify parent component about place type change
      if (onPlaceTypeChange) {
        onPlaceTypeChange(getPlaceTypeFromFilter(filter));
      }
    };

    // Get the place type from the selected filter
    const getPlaceTypeFromFilter = (filter: string) => {
      if (filter === "all") return "all";
      if (filter === "food") return "food";
      if (filter === "shopping") return "shopping";
      if (filter === "attraction") return "attraction";
      if (filter === "services") return "services";
      if (filter === "emergency") return "emergency";
      return "all";
    };

    // Use the places hook
    const { places, loading, hasMore, loadMore, reset } = usePlaces({
      propertyId: property.uid,
      placeType: placeType === "all" ? undefined : placeType,
      limit: 10,
      propertyLat: property.coordinates.lat,
      propertyLon: property.coordinates.lon,
    });
    
    return (
      <>
        <HeaderAndDescription headerInfo={property} headerType="property" />

        <PropertyTabs activeTab={activeTab} onTabChange={handleTabChange} />

        <div id="tab-content" className="w-full flex flex-col items-center gap-[16px]">
          {activeTab === "property" ? (
            <>
              {/*<PropertyPhotos 
                photos={property.photos.map(p => p.url)} 
              />*/}
              <PropertyButtons property={property} />
              <PropertyFAQs faqs={property.faq} />
            </>
          ) : activeTab === "places" ? (
            <div className="flex flex-col items-start gap-[16px] md:gap-[24px] w-full">
              <PlacesFilters
              categories={categories}
              selectedFilter={selectedFilter}
              onFilterChange={handleFilterChange}
              />

              <PlacesList
                places={places}
                hasMore={hasMore}
                loading={loading}
                onLoadMore={loadMore}
                onPlaceSelect={onPlaceSelect} // Pass handler
                onPlaceHover={onPlaceHover} // Pass hover handler
              />
            </div>
          ) : null}
        </div>
      </>
    );
};

export default PropertyContainer;